import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getWeeklyOrdersReport, 
  getDispatchSummaryReport, 
  getBatchRegisterReport,
  getAuditLogReport,
  getAuditLogFilters,
  getAuditLogStats,
  cleanupAuditLogs,
  getLabelPrintsReport,
  getTraceabilityReport,
  openTraceabilityPdf,
  getQCStatusReport,
  bulkApproveQC,
  getSalesByCategoryReport,
  openSalesByCategoryPdf,
  getRecallByBatch,
  getRecallByUseByDate,
  openRecallByBatchPdf,
  openRecallByUseByDatePdf,
  mintDownloadToken,
  sendRecallEmail,
  getOOSOrdersReport,
  getAccountsIQSyncErrors,
  retryAccountsIQError,
  resolveAccountsIQError,
  getTransportOptions,
  getProducts
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canManageUsers, formatDateTime } from '../lib/utils';
import { openPrintWindow } from '../lib/printWindow';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import { toast } from 'sonner';
import { Download, Loader2, FileText, Calendar, Package, History, Printer, Search, Link2, Eye, ExternalLink, X, Filter, Trash2, RotateCcw, ChevronLeft, ChevronRight, ShieldCheck, ShieldAlert, Clock, CheckCircle, CheckCircle2, ClipboardCheck, ChevronDown, ChevronUp, Tag, AlertTriangle, AlertCircle, Mail, Send, Truck, Plug } from 'lucide-react';

const DISPO_LABELS = { main_store: 'Main Store', shop: 'Shop Fridge', waste: 'Waste/Disposed' };

// CSV cell: quote, escape and neutralise Excel formula injection
const csvCell = (v) => {
  let s = v === null || v === undefined ? '' : String(v);
  if (/^[=+@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
};

const num = (v) => Number(v || 0);
// Dates are stored in both DD/MM/YYYY and ISO — render one format everywhere
const isoSort = (v) => {
  const s = String(v || '').slice(0, 10);
  return /^\d{2}\/\d{2}\/\d{4}$/.test(s) ? `${s.slice(6)}-${s.slice(3, 5)}-${s.slice(0, 2)}` : s;
};
const kgOrDash = (v) => (num(v) ? num(v).toFixed(2) : '—');
const fmtDate = (v) => {
  const s = String(v || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : (v || '');
};

export default function Reports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('traceability');  // Changed to load Trace first
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Traceability search
  const [traceSearch, setTraceSearch] = useState('');
  const [traceResults, setTraceResults] = useState([]);
  const [traceLoading, setTraceLoading] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState(null);

  // Audit log filters and pagination
  const [auditFilters, setAuditFilters] = useState({
    action: '',
    entity_type: '',
    user_email: '',
    date_from: '',
    date_to: ''
  });
  const [auditFilterOptions, setAuditFilterOptions] = useState({ actions: [], entity_types: [], user_emails: [] });
  const [auditStats, setAuditStats] = useState(null);
  const [auditPage, setAuditPage] = useState(0);
  const [auditTotal, setAuditTotal] = useState(0);
  const AUDIT_PAGE_SIZE = 50;

  // Batch Register filter + pagination
  const [batchRegSearch, setBatchRegSearch] = useState('');
  const [batchRegPage, setBatchRegPage] = useState(1);
  const BATCH_REG_PAGE_SIZE = 25;

  // QC Report state
  const [qcReport, setQcReport] = useState(null);
  const [qcDateFrom, setQcDateFrom] = useState('');
  const [qcDateTo, setQcDateTo] = useState('');
  const [qcSelectedBatches, setQcSelectedBatches] = useState(new Set());
  const [qcBulkApproving, setQcBulkApproving] = useState(false);

  // Sales by Category Report state
  const [salesByCatReport, setSalesByCatReport] = useState(null);
  const [salesByCatPeriod, setSalesByCatPeriod] = useState('week');
  const [salesByCatDateFrom, setSalesByCatDateFrom] = useState('');
  const [salesByCatDateTo, setSalesByCatDateTo] = useState('');
  const [salesByCatLoading, setSalesByCatLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Transport Report state
  const [transportDateFrom, setTransportDateFrom] = useState('');
  const [transportDateTo, setTransportDateTo] = useState('');
  const [transportAgent, setTransportAgent] = useState('all');
  const [transportLoading, setTransportLoading] = useState(false);
  // Transport options sourced from Settings → Transport Options
  // (tenant-configured list, not a hardcoded enum).
  const [transportOptions, setTransportOptions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await getTransportOptions();
        setTransportOptions(Array.isArray(r.data) ? r.data : []);
      } catch (_) { /* non-fatal — falls back to empty list */ }
    })();
  }, []);
  // Load products for the Recall Trace product-scope dropdown.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await getProducts({ active_only: true });
        const list = Array.isArray(data) ? data : (data.products || data.items || []);
        setRecallProductList(list);
      } catch (_) { /* non-fatal */ }
    })();
  }, []);
  const [expandedProducts, setExpandedProducts] = useState(new Set());

  // Recall Reporting state
  const [recallSearchType, setRecallSearchType] = useState('batch'); // 'batch' or 'usebydate'
  const [recallBatchCode, setRecallBatchCode] = useState('');
  const [recallProductId, setRecallProductId] = useState(''); // optional product scope
  const [recallProductList, setRecallProductList] = useState([]);
  const [recallUseByFrom, setRecallUseByFrom] = useState('');
  const [recallUseByTo, setRecallUseByTo] = useState('');
  const [recallResults, setRecallResults] = useState(null);
  const [recallLoading, setRecallLoading] = useState(false);
  
  // Recall Email Dialog state
  const [recallEmailDialogOpen, setRecallEmailDialogOpen] = useState(false);
  const [recallEmailRecipients, setRecallEmailRecipients] = useState('');
  const [recallEmailReason, setRecallEmailReason] = useState('');
  const [recallEmailSending, setRecallEmailSending] = useState(false);
  const [selectedRecallRecords, setSelectedRecallRecords] = useState([]);
  const [recallPerCustomerPdf, setRecallPerCustomerPdf] = useState(true);

  // OOS Orders Report state
  const [oosOrders, setOosOrders] = useState(null);
  const [oosDateFrom, setOosDateFrom] = useState('');
  const [oosDateTo, setOosDateTo] = useState('');
  const [oosLoading, setOosLoading] = useState(false);
  const [oosSearch, setOosSearch] = useState('');

  // AccountsIQ Sync Errors state — 3 sub-tabs (invoice / customer / product)
  const [aiqErrors, setAiqErrors] = useState({ rows: [], open_counts: {} });
  const [aiqErrorsLoading, setAiqErrorsLoading] = useState(false);
  const [aiqErrorsTab, setAiqErrorsTab] = useState('invoice');
  const [aiqErrorsStatus, setAiqErrorsStatus] = useState('open');
  const [aiqRowBusy, setAiqRowBusy] = useState(null);

  const loadAiqErrors = useCallback(async () => {
    setAiqErrorsLoading(true);
    try {
      const params = { category: aiqErrorsTab };
      if (aiqErrorsStatus !== 'all') params.status = aiqErrorsStatus;
      const { data } = await getAccountsIQSyncErrors(params);
      setAiqErrors(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load AccountsIQ sync errors');
    } finally {
      setAiqErrorsLoading(false);
    }
  }, [aiqErrorsTab, aiqErrorsStatus]);

  const handleAiqRetry = async (row) => {
    setAiqRowBusy(row.id);
    try {
      await retryAccountsIQError(row.id);
      toast.success('Retry queued / completed');
      await loadAiqErrors();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed');
    } finally {
      setAiqRowBusy(null);
    }
  };

  const handleAiqResolve = async (row) => {
    setAiqRowBusy(row.id);
    try {
      await resolveAccountsIQError(row.id);
      toast.success('Marked resolved');
      await loadAiqErrors();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resolve failed');
    } finally {
      setAiqRowBusy(null);
    }
  };

  const loadOOSOrders = async () => {
    setOosLoading(true);
    try {
      const params = {};
      if (oosDateFrom) params.start_date = oosDateFrom;
      if (oosDateTo) params.end_date = oosDateTo;
      const response = await getOOSOrdersReport(params);
      setOosOrders(response.data);
    } catch (e) {
      toast.error('Failed to load OOS orders');
    } finally {
      setOosLoading(false);
    }
  };

  // Navigate to that date in Order Pipeline (day view), parsing dd/MM/yyyy or yyyy-MM-dd
  const goToOOSOrderInPlanner = (order) => {
    if (!order?.dispatch_date) {
      toast.warning('This order has no dispatch date set');
      return;
    }
    // Convert dd/MM/yyyy → yyyy-MM-dd for the URL param
    let urlDate = order.dispatch_date;
    if (urlDate.includes('/')) {
      const [d, m, y] = urlDate.split('/');
      if (d && m && y) urlDate = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    navigate(`/planner?date=${urlDate}&dispatch=${order.dispatch_id}`);
  };

  const handleExportOOSCSV = () => {
    if (!oosOrders?.orders?.length) {
      toast.error('No OOS orders to export');
      return;
    }
    let csv = 'Dispatch Number,SO/PO Number,Customer,Dispatch Date,Status,OOS Lines,OOS Products\n';
    oosOrders.orders.forEach(o => {
      const products = (o.oos_products || []).join('; ').replace(/"/g, '""');
      csv += `"${o.dispatch_number}","${o.po_number || ''}","${o.customer_name}","${o.dispatch_date || ''}","${o.status}",${o.oos_count},"${products}"\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oos_orders_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  const loadSalesByCategoryReport = async () => {
    setSalesByCatLoading(true);
    try {
      const params = { period: salesByCatPeriod };
      if (salesByCatDateFrom) params.start_date = salesByCatDateFrom;
      if (salesByCatDateTo) params.end_date = salesByCatDateTo;
      const response = await getSalesByCategoryReport(params);
      setSalesByCatReport(response.data);
      // Expand all categories by default
      setExpandedCategories(new Set(response.data.categories?.map(c => c.category_name) || []));
    } catch (error) {
      toast.error('Failed to load report');
      console.error(error);
    } finally {
      setSalesByCatLoading(false);
    }
  };

  const toggleCategoryExpand = (catName) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(catName)) newSet.delete(catName);
      else newSet.add(catName);
      return newSet;
    });
  };

  const toggleProductExpand = (productId) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) newSet.delete(productId);
      else newSet.add(productId);
      return newSet;
    });
  };

  const downloadSalesByCategoryPdf = async () => {
    const params = { period: salesByCatPeriod };
    if (salesByCatDateFrom) params.start_date = salesByCatDateFrom;
    if (salesByCatDateTo) params.end_date = salesByCatDateTo;
    try {
      await openSalesByCategoryPdf(params);
    } catch {
      toast.error('Failed to prepare PDF');
    }
  };

  // Recall Report Functions
  const loadRecallReport = async () => {
    if (recallSearchType === 'batch' && !recallBatchCode.trim()) {
      toast.error('Please enter a batch code');
      return;
    }
    if (recallSearchType === 'usebydate' && !recallUseByFrom) {
      toast.error('Please select a use-by date');
      return;
    }

    setRecallLoading(true);
    setRecallResults(null);
    try {
      let response;
      if (recallSearchType === 'batch') {
        response = await getRecallByBatch(recallBatchCode.trim(), recallProductId || undefined);
      } else {
        response = await getRecallByUseByDate(recallUseByFrom, recallUseByTo);
      }
      setRecallResults(response.data);
      if (response.data.total_records === 0) {
        toast.info('No records found');
      } else {
        toast.success(`Found ${response.data.total_records} record(s) affecting ${response.data.total_customers_affected} customer(s)`);
        // Cross-product collision warning
        const distinct = response.data.distinct_products || [];
        if (recallSearchType === 'batch' && !recallProductId && distinct.length > 1) {
          toast.warning(
            `Batch "${recallBatchCode}" matched ${distinct.length} different products: ${distinct.slice(0, 3).join(', ')}${distinct.length > 3 ? '…' : ''}. Pick a Product to scope the trace.`,
            { duration: 10000 }
          );
        }
      }
    } catch (error) {
      toast.error('Failed to load recall report');
      console.error(error);
    } finally {
      setRecallLoading(false);
    }
  };

  const downloadRecallPdf = async () => {
    try {
      if (recallSearchType === 'batch') {
        await openRecallByBatchPdf(recallBatchCode, recallProductId || undefined);
      } else {
        await openRecallByUseByDatePdf(recallUseByFrom, recallUseByTo);
      }
    } catch {
      toast.error('Failed to prepare recall PDF');
    }
  };

  const exportRecallCSV = () => {
    if (!recallResults?.results?.length) {
      toast.error('No data to export');
      return;
    }

    let csvContent = 'Production Batch Code,Sale Date,Sales Invoice Number,Customer Name,Purchase Order Reference,Product Name,Product Format/Brand,Volume (kg),Cases,Units,Qty Dispatched,Qty Returned,Returned To,Return Numbers,Net Qty Out,Net Volume (kg),Use By Date,Customer Product Code\n';
    recallResults.results.forEach(r => {
      const returnedTo = Object.entries(r.returned_to || {}).map(([k, v]) => `${DISPO_LABELS[k] || k}: ${v}`).join('; ');
      csvContent += `"${r.production_batch_code}","${r.sale_date}","${r.sales_invoice_number}","${r.customer_name}","${r.purchase_order_reference}","${r.product_name}","${r.product_format_brand}",${(r.volume_kg || 0).toFixed(2)},${(r.cases || 0).toFixed(2)},${r.units || 0},${r.quantity_dispatched ?? ''},${r.quantity_returned ?? 0},"${returnedTo}","${(r.return_numbers || []).join('; ')}",${r.net_quantity ?? ''},${(r.net_volume_kg ?? 0).toFixed(2)},"${r.use_by_date}","${r.customer_product_code}"\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recall_trace_${recallSearchType}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  // Open recall email dialog with preloaded customer emails
  // Includes BOTH the customer's Incident Management email AND their primary
  // contact email (and any extras) for each affected customer — operator can
  // edit/remove any address before sending.
  const handleOpenRecallEmailDialog = () => {
    if (!recallResults?.results?.length) {
      toast.error('No results to email');
      return;
    }
    const seen = new Set();
    const ordered = [];
    recallResults.results.forEach((r) => {
      // Prefer the new customer_emails[] array; fall back to legacy single field
      const list = Array.isArray(r.customer_emails) && r.customer_emails.length > 0
        ? r.customer_emails
        : (r.customer_email ? [r.customer_email] : []);
      list.forEach((email) => {
        const k = (email || '').trim().toLowerCase();
        if (k && k.includes('@') && !seen.has(k)) {
          seen.add(k);
          ordered.push(email.trim());
        }
      });
    });
    if (ordered.length === 0) {
      toast.warning(
        'No customer emails on file for any affected customer. Add an Incident Management or Contact email on the Customers page first.',
        { duration: 7000 },
      );
    }
    setRecallEmailRecipients(ordered.join(', '));
    setRecallEmailReason('');
    setRecallEmailDialogOpen(true);
  };

  // Send recall notification emails
  const handleSendRecallEmail = async () => {
    if (!recallEmailRecipients.trim()) {
      toast.error('Please enter at least one recipient email');
      return;
    }
    if (!recallEmailReason.trim()) {
      toast.error('Please provide a reason for the recall');
      return;
    }

    const recipients = recallEmailRecipients
      .split(/[,;\n]+/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));

    if (recipients.length === 0) {
      toast.error('No valid email addresses found');
      return;
    }

    setRecallEmailSending(true);
    try {
      const response = await sendRecallEmail({
        recipients,
        reason: recallEmailReason,
        search_type: recallSearchType,
        search_value: recallSearchType === 'batch' ? recallBatchCode : `${recallUseByFrom} to ${recallUseByTo || recallUseByFrom}`,
        per_customer_pdf: recallPerCustomerPdf,
        affected_records: recallResults.results.map(r => ({
          customer_name: r.customer_name,
          customer_email: r.customer_email,
          customer_emails: r.customer_emails || [],
          product_name: r.product_name,
          batch_code: r.production_batch_code,
          use_by_date: r.use_by_date,
          volume_kg: r.volume_kg,
          units: r.units,
          cases: r.cases,
          sales_invoice_number: r.sales_invoice_number,
          purchase_order_reference: r.purchase_order_reference,
          customer_product_code: r.customer_product_code,
        }))
      });

      if (response.data.sent > 0) {
        toast.success(`Recall emails sent to ${response.data.sent} recipient(s)`);
      }
      if (response.data.failed > 0) {
        // Surface the first concrete SMTP / recipient error so the operator
        // can act (e.g. fix SMTP password, fix recipient address, etc.).
        const failedList = response.data.failed_recipients || [];
        const firstErr = failedList[0]?.error;
        toast.error(
          <div>
            <p className="font-semibold">Failed to send to {response.data.failed} recipient(s)</p>
            {firstErr && (
              <p className="text-xs mt-1 break-words">{firstErr}</p>
            )}
            {failedList.length > 1 && (
              <p className="text-xs mt-1 opacity-80">(+ {failedList.length - 1} more — check Reports → Audit Log)</p>
            )}
          </div>,
          { duration: 10000 }
        );
      }
      // Only auto-close the dialog if everything was sent
      if (response.data.sent > 0 && response.data.failed === 0) {
        setRecallEmailDialogOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send recall emails');
    } finally {
      setRecallEmailSending(false);
    }
  };

  const loadReport = async (reportType) => {
    setLoading(true);
    setData(null);
    try {
      let response;
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      switch (reportType) {
        case 'weekly':
          response = await getWeeklyOrdersReport();
          break;
        case 'summary':
          response = await getDispatchSummaryReport(params);
          break;
        case 'batches':
          response = await getBatchRegisterReport();
          break;
        case 'audit':
          // Load filter options and stats first time
          if (!auditFilterOptions.actions.length) {
            const filtersRes = await getAuditLogFilters();
            setAuditFilterOptions(filtersRes.data);
          }
          const statsRes = await getAuditLogStats();
          setAuditStats(statsRes.data);
          // Load logs with filters
          const auditParams = { limit: AUDIT_PAGE_SIZE, offset: auditPage * AUDIT_PAGE_SIZE };
          if (auditFilters.action) auditParams.action = auditFilters.action;
          if (auditFilters.entity_type) auditParams.entity_type = auditFilters.entity_type;
          if (auditFilters.user_email) auditParams.user_email = auditFilters.user_email;
          if (auditFilters.date_from) auditParams.date_from = auditFilters.date_from;
          if (auditFilters.date_to) auditParams.date_to = auditFilters.date_to;
          response = await getAuditLogReport(auditParams);
          setAuditTotal(response.data.total);
          setData(response.data.logs);
          setLoading(false);
          return;
        case 'qc':
          const qcParams = {};
          if (qcDateFrom) qcParams.date_from = qcDateFrom;
          if (qcDateTo) qcParams.date_to = qcDateTo;
          response = await getQCStatusReport(qcParams);
          setQcReport(response.data);
          setQcSelectedBatches(new Set());
          setLoading(false);
          return;
        case 'labels':
          response = await getLabelPrintsReport(params);
          break;
        case 'traceability':
          // Don't auto-load, wait for search
          setLoading(false);
          return;
        default:
          return;
      }
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // Bulk approve QC
  const handleBulkApproveQC = async () => {
    if (qcSelectedBatches.size === 0) {
      toast.error('No batches selected');
      return;
    }
    
    setQcBulkApproving(true);
    try {
      const result = await bulkApproveQC({ batch_ids: Array.from(qcSelectedBatches) });
      toast.success(result.data.message);
      setQcSelectedBatches(new Set());
      loadReport('qc');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve batches');
    } finally {
      setQcBulkApproving(false);
    }
  };

  // Toggle batch selection for QC
  const toggleQCBatchSelection = (batchId) => {
    setQcSelectedBatches(prev => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  };

  // Select all on-hold batches for a date
  const selectAllOnHoldForDate = (batches) => {
    const onHoldIds = batches.filter(b => b.qc_status === 'on_hold').map(b => b.id);
    setQcSelectedBatches(prev => {
      const next = new Set(prev);
      onHoldIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleTraceabilitySearch = async () => {
    if (!traceSearch.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    setTraceLoading(true);
    try {
      const response = await getTraceabilityReport({ search: traceSearch.trim() });
      setTraceResults(response.data);
      if (response.data.length === 0) {
        toast.info('No matching dispatches found');
      } else {
        toast.success(`Found ${response.data.length} dispatch(es)`);
      }
    } catch (error) {
      toast.error('Failed to search');
    } finally {
      setTraceLoading(false);
    }
  };

  // Flatten search results to one row per BATCH CODE per order (true sub-batch figures)
  const traceBatchRows = useCallback(() => {
    const rows = [];
    traceResults.forEach(d => {
      (d.batch_groups || []).forEach(g => rows.push({
        ...g,
        weight_kg: g.report_weight_kg ?? g.weight_kg,
        dispatch_number: d.dispatch_number,
        customer_name: d.customer_name,
        dispatch_date: d.dispatch_date_display || d.dispatch_date,
        po_number: d.po_number,
        status: d.status,
      }));
    });
    rows.sort((a, b) =>
      String(a.batch_code || '').localeCompare(String(b.batch_code || '')) ||
      isoSort(b.dispatch_date).localeCompare(isoSort(a.dispatch_date))
    );
    return rows;
  }, [traceResults]);

  const traceTotals = React.useMemo(() => {
    let cases = 0, kg = 0;
    const codes = new Set();
    traceResults.forEach(d => (d.batch_groups || []).forEach(g => {
      cases += num(g.cases);
      kg += num(g.report_weight_kg ?? g.weight_kg);
      if (g.batch_code) codes.add(g.batch_code);
    }));
    return { cases, kg, batchCodes: codes.size };
  }, [traceResults]);

  useEffect(() => {
    loadReport(activeTab);
    // Reset Batch Register's search + page when entering the tab
    if (activeTab === 'batches') {
      setBatchRegSearch('');
      setBatchRegPage(1);
    }
    if (activeTab === 'oos' && !oosOrders) {
      loadOOSOrders();
    }
    if (activeTab === 'aiq-errors') {
      loadAiqErrors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Export traceability results to CSV — the agreed TraceBack structure:
  // one row per batch code per product per order (distinct products never
  // merged), showing only the cases + weight recorded against that batch.
  const handleExportTraceCSV = () => {
    const rows = traceBatchRows();
    if (rows.length === 0) {
      toast.error('No results to export');
      return;
    }

    const header = 'Batch Number,Dispatch Number,Customer,Product description,Customer Product Code,Branding,Pack size,Dispatch Date,PO Number,Status,Cases,Weight (kg)\n';
    const body = rows.map(r => [
      csvCell(r.batch_code),
      csvCell(r.dispatch_number),
      csvCell(r.customer_name),
      csvCell(r.product_name),
      csvCell(r.customer_product_code),
      csvCell(r.brand),
      csvCell(r.pack_size),
      csvCell(r.dispatch_date),
      csvCell(r.po_number),
      csvCell(r.status),
      Number(r.cases || 0).toFixed(2),
      Number(r.weight_kg || 0).toFixed(2),
    ].join(',')).join('\n');

    const blob = new Blob([header + body + '\n'], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch_traceability_${(traceSearch || 'all').replace(/[^\w-]/g, '')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} batch row(s)`);
  };

  // Batch-level PDF for auditors (server-rendered, paginated)
  const handleExportTracePDF = async () => {
    if (!traceSearch.trim()) {
      toast.error('Run a search first');
      return;
    }
    try {
      await openTraceabilityPdf({ search: traceSearch.trim() });
    } catch (e) {
      toast.error('Failed to generate PDF');
    }
  };

  // Export QC report to CSV
  const handleExportQCCSV = () => {
    if (!qcReport?.by_date?.length) {
      toast.error('No QC data to export');
      return;
    }
    
    let csvContent = 'Production Date,Batch Code,Product,Use By,Quantity,Weight (kg),Cases,QC Status\n';
    qcReport.by_date.forEach(dayData => {
      dayData.batches.forEach(batch => {
        csvContent += `"${dayData.date}","${batch.batch_code}","${batch.product_name}","${batch.use_by_date || ''}",${batch.quantity || 0},${batch.weight_kg || 0},${batch.cases || 0},"${batch.qc_status}"\n`;
      });
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qc_status_report.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  // Export QC report to PDF
  const handleExportQCPDF = () => {
    if (!qcReport?.by_date?.length) {
      toast.error('No QC data to export');
      return;
    }
    
    const printContent = `
      <html>
        <head>
          <title>QC Status Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 20px; margin-bottom: 5px; }
            .summary { display: flex; gap: 20px; margin: 15px 0; padding: 10px; background: #f5f5f5; }
            .summary-item { text-align: center; }
            .summary-value { font-size: 24px; font-weight: bold; }
            .summary-label { font-size: 11px; color: #666; }
            h2 { font-size: 14px; margin-top: 20px; margin-bottom: 8px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 15px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background: #f5f5f5; }
            .status-on_hold { background: #fef3c7; color: #92400e; }
            .status-released { background: #d1fae5; color: #065f46; }
            .status-rejected { background: #fee2e2; color: #991b1b; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>QC Status Report</h1>
          <div class="summary">
            <div class="summary-item"><div class="summary-value">${qcReport.summary?.total_batches || 0}</div><div class="summary-label">Total Batches</div></div>
            <div class="summary-item"><div class="summary-value" style="color:#92400e">${qcReport.summary?.on_hold || 0}</div><div class="summary-label">On Hold</div></div>
            <div class="summary-item"><div class="summary-value" style="color:#065f46">${qcReport.summary?.released || 0}</div><div class="summary-label">Released</div></div>
            <div class="summary-item"><div class="summary-value" style="color:#991b1b">${qcReport.summary?.rejected || 0}</div><div class="summary-label">Rejected</div></div>
          </div>
          ${qcReport.by_date.map(dayData => `
            <h2>${dayData.date}</h2>
            <table>
              <tr><th>Batch Code</th><th>Product</th><th>Use By</th><th>Qty</th><th>Weight (kg)</th><th>Cases</th><th>Status</th></tr>
              ${dayData.batches.map(b => `
                <tr>
                  <td>${b.batch_code}</td>
                  <td>${b.product_name}</td>
                  <td>${b.use_by_date || '-'}</td>
                  <td>${(b.quantity || 0).toFixed(0)}</td>
                  <td>${(b.weight_kg || 0).toFixed(2)}</td>
                  <td>${(b.cases || 0).toFixed(0)}</td>
                  <td class="status-${b.qc_status}">${b.qc_status}</td>
                </tr>
              `).join('')}
            </table>
          `).join('')}
        </body>
      </html>
    `;
    
    openPrintWindow(printContent);
  };

  // Export Weekly Orders to CSV
  const handleExportWeeklyCSV = () => {
    if (!data || !Array.isArray(data)) {
      toast.error('No data to export');
      return;
    }
    
    let csvContent = 'Week,Year,Dispatch Number,Customer,Date,Cases,Weight (kg),Status\n';
    data.forEach(week => {
      week.dispatches?.forEach(d => {
        csvContent += `${week.week},${week.year},"${d.dispatch_number}","${d.customer_name}","${d.dispatch_date}",${(d.total_cases || 0).toFixed(2)},${(d.total_weight || 0).toFixed(2)},"${d.status}"\n`;
      });
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `weekly_orders_report.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  // Export Weekly Orders to PDF
  const handleExportWeeklyPDF = () => {
    if (!data || !Array.isArray(data)) {
      toast.error('No data to export');
      return;
    }
    
    const printContent = `
      <html>
        <head>
          <title>Weekly Orders Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 20px; margin-bottom: 10px; }
            h2 { font-size: 14px; background: #f5f5f5; padding: 8px; margin-top: 15px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background: #f0f0f0; }
            .totals { font-weight: bold; background: #f9f9f9; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Weekly Orders Report</h1>
          ${data.map(week => `
            <h2>Week ${week.week}, ${week.year} - ${week.total_dispatches} dispatches, ${(week.total_cases || 0).toFixed(0)} cases, ${(week.total_weight || 0).toFixed(2)} kg</h2>
            <table>
              <tr><th>Dispatch #</th><th>Customer</th><th>Date</th><th>Cases</th><th>Weight</th><th>Status</th></tr>
              ${week.dispatches?.map(d => `
                <tr>
                  <td>${d.dispatch_number}</td>
                  <td>${d.customer_name}</td>
                  <td>${d.dispatch_date}</td>
                  <td>${(d.total_cases || 0).toFixed(2)}</td>
                  <td>${(d.total_weight || 0).toFixed(2)} kg</td>
                  <td>${d.status}</td>
                </tr>
              `).join('')}
            </table>
          `).join('')}
        </body>
      </html>
    `;
    
    openPrintWindow(printContent);
  };

  // Export Dispatch Summary to CSV
  const handleExportSummaryCSV = () => {
    if (!data) {
      toast.error('No data to export');
      return;
    }
    
    let csvContent = 'DISPATCH SUMMARY REPORT\n\n';
    csvContent += `Total Dispatches,${data.total_dispatches}\n`;
    csvContent += `Total Cases,${data.total_cases?.toFixed(2)}\n`;
    csvContent += `Total Weight (kg),${data.total_weight?.toFixed(2)}\n\n`;
    
    csvContent += 'BY CUSTOMER\n';
    csvContent += 'Customer,Dispatches,Cases,Weight (kg)\n';
    data.by_customer?.forEach(c => {
      csvContent += `"${c.customer}",${c.dispatches},${(c.cases || 0).toFixed(2)},${(c.weight || 0).toFixed(2)}\n`;
    });
    
    csvContent += '\nBY PRODUCT\n';
    csvContent += 'Product,Quantity,Cases,Weight (kg)\n';
    data.by_product?.forEach(p => {
      csvContent += `"${p.product}",${(p.quantity || 0).toFixed(2)},${(p.cases || 0).toFixed(2)},${(p.weight || 0).toFixed(2)}\n`;
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispatch_summary_report.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  // Export Dispatch Summary to PDF
  const handleExportSummaryPDF = () => {
    if (!data) {
      toast.error('No data to export');
      return;
    }
    
    const printContent = `
      <html>
        <head>
          <title>Dispatch Summary Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 20px; margin-bottom: 15px; }
            h2 { font-size: 14px; margin-top: 20px; margin-bottom: 8px; }
            .summary { display: flex; gap: 30px; margin: 15px 0; }
            .summary-item { background: #f5f5f5; padding: 15px; border-radius: 4px; }
            .summary-value { font-size: 28px; font-weight: bold; }
            .summary-label { font-size: 11px; color: #666; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background: #f0f0f0; }
            .right { text-align: right; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Dispatch Summary Report</h1>
          <div class="summary">
            <div class="summary-item"><div class="summary-value">${data.total_dispatches}</div><div class="summary-label">Total Dispatches</div></div>
            <div class="summary-item"><div class="summary-value">${(data.total_cases || 0).toFixed(0)}</div><div class="summary-label">Total Cases</div></div>
            <div class="summary-item"><div class="summary-value">${(data.total_weight || 0).toFixed(2)} kg</div><div class="summary-label">Total Weight</div></div>
          </div>
          
          <h2>By Customer</h2>
          <table>
            <tr><th>Customer</th><th class="right">Dispatches</th><th class="right">Cases</th><th class="right">Weight</th></tr>
            ${data.by_customer?.map(c => `
              <tr><td>${c.customer}</td><td class="right">${c.dispatches}</td><td class="right">${(c.cases || 0).toFixed(2)}</td><td class="right">${(c.weight || 0).toFixed(2)} kg</td></tr>
            `).join('')}
          </table>
          
          <h2>By Product</h2>
          <table>
            <tr><th>Product</th><th class="right">Quantity</th><th class="right">Cases</th><th class="right">Weight</th></tr>
            ${data.by_product?.map(p => `
              <tr><td>${p.product}</td><td class="right">${(p.quantity || 0).toFixed(2)}</td><td class="right">${(p.cases || 0).toFixed(2)}</td><td class="right">${(p.weight || 0).toFixed(2)} kg</td></tr>
            `).join('')}
          </table>
        </body>
      </html>
    `;
    
    openPrintWindow(printContent);
  };

  const handleExportCSV = () => {
    if (!data) return;
    
    let csvContent = '';
    
    if (activeTab === 'batches' && Array.isArray(data)) {
      csvContent = 'Batch Code,Product,SKU,Format,Production Date,Use By,Produced,Available,Allocated,Dispatched\n';
      data.forEach(row => {
        csvContent += `${row.batch_code},${row.product_name},${row.product_sku},${row.product_format},${row.production_date},${row.use_by_date},${row.quantity_produced},${row.quantity_available},${row.quantity_allocated},${row.quantity_dispatched}\n`;
      });
    }

    if (csvContent) {
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}_report.csv`;
      a.click();
    }
  };

  const downloadTransportReport = async (format) => {
    if (!transportDateFrom || !transportDateTo) {
      toast.error('Please select both start and end dates');
      return;
    }
    setTransportLoading(true);
    try {
      // SEC-001: mint a single-use download token instead of embedding the
      // session JWT into the file link.
      const dlt = await mintDownloadToken('transport_report');
      const params = new URLSearchParams({
        start_date: transportDateFrom,
        end_date: transportDateTo,
        format,
        token: dlt,
      });
      if (transportAgent && transportAgent !== 'all') {
        params.set('transport_agent', transportAgent);
      }
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/transport?${params}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = `transport_report_${transportDateFrom}_to_${transportDateTo}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`Transport report downloading (${format.toUpperCase()})`);
    } catch (e) {
      toast.error('Failed to download transport report');
    } finally {
      setTransportLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1 w-full max-w-6xl h-auto p-1">
          <TabsTrigger value="traceability" className="flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            <span className="hidden sm:inline">Trace</span>
          </TabsTrigger>
          <TabsTrigger value="recall" className="flex items-center gap-2 bg-red-50 data-[state=active]:bg-red-100">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="hidden sm:inline text-red-700">Recall</span>
          </TabsTrigger>
          <TabsTrigger value="oos" className="flex items-center gap-2 bg-amber-50 data-[state=active]:bg-amber-100" data-testid="tab-oos-orders">
            <Package className="w-4 h-4 text-amber-700" />
            <span className="hidden sm:inline text-amber-800">OOS Orders</span>
          </TabsTrigger>
          <TabsTrigger value="aiq-errors" className="flex items-center gap-2 bg-indigo-50 data-[state=active]:bg-indigo-100" data-testid="tab-aiq-errors">
            <AlertCircle className="w-4 h-4 text-indigo-700" />
            <span className="hidden sm:inline text-indigo-800">Sync Errors</span>
          </TabsTrigger>
          <TabsTrigger value="qc" className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            <span className="hidden sm:inline">QC</span>
          </TabsTrigger>
          <TabsTrigger value="compliance" className="flex items-center gap-2" onClick={() => navigate('/reports/compliance')}>
            <ClipboardCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Compliance</span>
          </TabsTrigger>
          <TabsTrigger value="weekly" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">Weekly</span>
          </TabsTrigger>
          <TabsTrigger value="summary" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Summary</span>
          </TabsTrigger>
          <TabsTrigger value="batches" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Batches</span>
          </TabsTrigger>
          <TabsTrigger value="sales-category" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            <span className="hidden sm:inline">Sales/Cat</span>
          </TabsTrigger>
          <TabsTrigger value="labels" className="flex items-center gap-2">
            <Printer className="w-4 h-4" />
            <span className="hidden sm:inline">Labels</span>
          </TabsTrigger>
          <TabsTrigger value="transport" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            <span className="hidden sm:inline">Transport</span>
          </TabsTrigger>
          {canManageUsers(user?.role) && (
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">Audit</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* QC Status Report */}
        <TabsContent value="qc" className="mt-6 space-y-4">
          {/* QC Filters */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldAlert className="w-4 h-4" />
                QC Status Report
              </CardTitle>
              <CardDescription>
                View production batches and their QC status. Bulk approve pending QC items.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <Label className="text-xs">From Date</Label>
                  <DatePicker
                    value={qcDateFrom}
                    onChange={setQcDateFrom}
                    placeholder="From date"
                    className="w-40"
                  />
                </div>
                <div>
                  <Label className="text-xs">To Date</Label>
                  <DatePicker
                    value={qcDateTo}
                    onChange={setQcDateTo}
                    placeholder="To date"
                    className="w-40"
                  />
                </div>
                <Button size="sm" onClick={() => loadReport('qc')}>
                  <Search className="w-4 h-4 mr-1" /> Load Report
                </Button>
                {qcReport?.by_date?.length > 0 && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleExportQCCSV}>
                      <Download className="w-4 h-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportQCPDF}>
                      <Printer className="w-4 h-4 mr-1" /> Print/PDF
                    </Button>
                  </>
                )}
                {qcSelectedBatches.size > 0 && (
                  <Button 
                    size="sm" 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleBulkApproveQC}
                    disabled={qcBulkApproving}
                  >
                    {qcBulkApproving ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-1" />
                    )}
                    Approve Selected ({qcSelectedBatches.size})
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* QC Summary */}
          {qcReport?.summary && (
            <Card>
              <CardContent className="py-4">
                <div className="grid grid-cols-2 md:grid-cols-7 gap-4 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Total Batches</p>
                    <p className="text-2xl font-bold font-mono">{qcReport.summary.total_batches}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">On Hold</p>
                    <p className="text-2xl font-bold font-mono text-amber-600">{qcReport.summary.on_hold}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Released</p>
                    <p className="text-2xl font-bold font-mono text-emerald-600">{qcReport.summary.released}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Rejected</p>
                    <p className="text-2xl font-bold font-mono text-red-600">{qcReport.summary.rejected}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Qty</p>
                    <p className="text-2xl font-bold font-mono">{qcReport.summary.total_quantity?.toFixed(0) || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Weight</p>
                    <p className="text-2xl font-bold font-mono text-blue-700">{qcReport.summary.total_weight?.toFixed(1) || 0} kg</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Cases</p>
                    <p className="text-2xl font-bold font-mono">{qcReport.summary.total_cases?.toFixed(0) || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* QC by Date */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : qcReport?.by_date && qcReport.by_date.length > 0 ? (
            <div className="space-y-4">
              {qcReport.by_date.map((dayData) => (
                <Card key={dayData.date}>
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-base font-mono flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {dayData.date}
                        </CardTitle>
                        <span className="text-xs text-muted-foreground font-mono">
                          ({dayData.total_quantity?.toFixed(0) || 0} qty / {dayData.total_weight?.toFixed(1) || 0} kg / {dayData.total_cases?.toFixed(0) || 0} cases)
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-amber-600" />
                          {dayData.on_hold} hold
                        </span>
                        <span className="flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3 text-emerald-600" />
                          {dayData.released} released
                        </span>
                        {dayData.rejected > 0 && (
                          <span className="flex items-center gap-1 text-red-600">
                            <X className="w-3 h-3" />
                            {dayData.rejected} rejected
                          </span>
                        )}
                        {dayData.on_hold > 0 && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => selectAllOnHoldForDate(dayData.batches)}
                          >
                            Select All On Hold
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="w-10 px-3 py-2"></th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Batch Code</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Product</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Use By</th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Qty</th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Weight</th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Cases</th>
                          <th className="text-center px-3 py-2 text-xs font-medium text-muted-foreground">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {dayData.batches.map((batch) => (
                          <tr key={batch.id} className={`hover:bg-muted/30 ${qcSelectedBatches.has(batch.id) ? 'bg-primary/10' : ''}`}>
                            <td className="px-3 py-2 text-center">
                              {batch.qc_status === 'on_hold' && (
                                <Checkbox
                                  checked={qcSelectedBatches.has(batch.id)}
                                  onCheckedChange={() => toggleQCBatchSelection(batch.id)}
                                />
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono">{batch.batch_code}</td>
                            <td className="px-3 py-2">{batch.product_name}</td>
                            <td className="px-3 py-2 font-mono text-xs">{batch.use_by_date || '-'}</td>
                            <td className="px-3 py-2 text-right font-mono">{(batch.quantity || 0).toFixed(0)}</td>
                            <td className="px-3 py-2 text-right font-mono text-blue-700">{(batch.weight_kg || 0).toFixed(2)} kg</td>
                            <td className="px-3 py-2 text-right font-mono">{(batch.cases || 0).toFixed(0)}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                batch.qc_status === 'released' ? 'bg-emerald-100 text-emerald-700' :
                                batch.qc_status === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {batch.qc_status === 'released' ? <ShieldCheck className="w-3 h-3" /> :
                                 batch.qc_status === 'rejected' ? <X className="w-3 h-3" /> :
                                 <Clock className="w-3 h-3" />}
                                {batch.qc_status === 'on_hold' ? 'On Hold' : batch.qc_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : qcReport ? (
            <p className="text-center text-muted-foreground py-8">No batches found for the selected date range</p>
          ) : (
            <p className="text-center text-muted-foreground py-8">Click &ldquo;Load Report&rdquo; to view QC status</p>
          )}
        </TabsContent>

        {/* Recall Reporting */}
        <TabsContent value="recall" className="mt-6 space-y-4">
          <Card className="border-red-200">
            <CardHeader className="py-4 bg-red-50">
              <CardTitle className="text-base flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-5 h-5" />
                Recall Trace Enquiry
              </CardTitle>
              <CardDescription className="text-red-700">
                Emergency recall reporting - trace products by batch code or use-by date range
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {/* Search Type Selection */}
              <div className="flex flex-wrap gap-4 items-end mb-4">
                <div>
                  <Label className="text-xs">Search By</Label>
                  <Select value={recallSearchType} onValueChange={setRecallSearchType}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="batch">Production Batch Code</SelectItem>
                      <SelectItem value="usebydate">Use By Date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {recallSearchType === 'batch' ? (
                  <>
                    <div className="flex-1 min-w-[200px]">
                      <Label className="text-xs">Batch Code</Label>
                      <Input
                        value={recallBatchCode}
                        onChange={(e) => setRecallBatchCode(e.target.value)}
                        placeholder="Enter batch code to trace..."
                        onKeyDown={(e) => e.key === 'Enter' && loadRecallReport()}
                        data-testid="recall-trace-batch-code"
                      />
                    </div>
                    <div className="min-w-[240px]">
                      <Label className="text-xs flex items-center gap-1">
                        Product
                        <span className="text-[10px] text-muted-foreground font-normal">(scope — avoids batch-code collisions)</span>
                      </Label>
                      <Select value={recallProductId || 'all'} onValueChange={v => setRecallProductId(v === 'all' ? '' : v)}>
                        <SelectTrigger data-testid="recall-trace-product-id">
                          <SelectValue placeholder="All products" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All products (no scope)</SelectItem>
                          {recallProductList.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}{p.sku ? ` — ${p.sku}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs">Use By Date From</Label>
                      <DatePicker
                        value={recallUseByFrom}
                        onChange={setRecallUseByFrom}
                        placeholder="From date"
                        className="w-40"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Use By Date To (optional)</Label>
                      <DatePicker
                        value={recallUseByTo}
                        onChange={setRecallUseByTo}
                        placeholder="To date"
                        className="w-40"
                      />
                    </div>
                  </>
                )}

                <Button onClick={loadRecallReport} disabled={recallLoading} className="bg-red-600 hover:bg-red-700">
                  {recallLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                  Search
                </Button>

                {recallResults?.results?.length > 0 && (
                  <>
                    <Button variant="outline" size="sm" onClick={exportRecallCSV} data-testid="btn-recall-export-csv">
                      <Download className="w-4 h-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadRecallPdf} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="btn-recall-view-pdf">
                      <Eye className="w-4 h-4 mr-1" /> View PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleOpenRecallEmailDialog} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="btn-recall-mail-pdf">
                      <Mail className="w-4 h-4 mr-1" /> Mail
                    </Button>
                  </>
                )}
              </div>

              {/* Results Summary */}
              {recallResults && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex flex-wrap gap-6 text-sm">
                    <div>
                      <span className="text-red-700 font-medium">Total Records:</span>{' '}
                      <span className="font-bold text-red-900">{recallResults.total_records}</span>
                    </div>
                    <div>
                      <span className="text-red-700 font-medium">Customers Affected:</span>{' '}
                      <span className="font-bold text-red-900">{recallResults.total_customers_affected}</span>
                    </div>
                    <div>
                      <span className="text-red-700 font-medium">Total Volume:</span>{' '}
                      <span className="font-bold text-red-900">{recallResults.total_volume_kg?.toFixed(2)} kg</span>
                    </div>
                    {(recallResults.total_quantity_returned || 0) > 0 && (
                      <>
                        <div data-testid="recall-summary-returned">
                          <span className="text-amber-700 font-medium">Returned to Premises:</span>{' '}
                          <span className="font-bold text-amber-900">{recallResults.total_quantity_returned}</span>
                        </div>
                        <div data-testid="recall-summary-net">
                          <span className="text-red-700 font-medium">Net Volume Still Out:</span>{' '}
                          <span className="font-bold text-red-900">{recallResults.net_volume_kg?.toFixed(2)} kg</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Results Table */}
              {recallLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-red-600" />
                </div>
              ) : recallResults?.results?.length > 0 ? (
                <>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-red-100 border-b border-red-200">
                        <th className="text-left p-2 font-medium text-red-800">Batch Code</th>
                        <th className="text-left p-2 font-medium text-red-800">Sale Date</th>
                        <th className="text-left p-2 font-medium text-red-800">Invoice #</th>
                        <th className="text-left p-2 font-medium text-red-800">Customer</th>
                        <th className="text-left p-2 font-medium text-red-800">PO Ref</th>
                        <th className="text-left p-2 font-medium text-red-800">Product</th>
                        <th className="text-right p-2 font-medium text-red-800">Vol (kg)</th>
                        <th className="text-right p-2 font-medium text-red-800">Qty</th>
                        <th className="text-right p-2 font-medium text-red-800">Returned</th>
                        <th className="text-right p-2 font-medium text-red-800">Net Out</th>
                        <th className="text-left p-2 font-medium text-red-800">Use By</th>
                        <th className="text-left p-2 font-medium text-red-800">Cust Code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recallResults.results.map((r, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-red-50/30'}>
                          <td className="p-2 font-mono text-xs">{r.production_batch_code}</td>
                          <td className="p-2">{r.sale_date}</td>
                          <td className="p-2 font-mono">
                            {r.sales_invoice_number}
                            {r.dispatch_status === 'reversed' && (
                              <span className="ml-1 text-[10px] text-amber-700 font-sans font-medium">(reversed)</span>
                            )}
                          </td>
                          <td className="p-2 font-medium">{r.customer_name}</td>
                          <td className="p-2">{r.purchase_order_reference}</td>
                          <td className="p-2">{r.product_name}</td>
                          <td className="p-2 text-right font-mono">{r.volume_kg?.toFixed(2)}</td>
                          <td className="p-2 text-right font-mono">{r.quantity_dispatched}</td>
                          <td className="p-2 text-right font-mono">
                            {(r.quantity_returned || 0) > 0 ? (
                              <span
                                className="text-amber-700 font-semibold"
                                title={`${(r.return_numbers || []).join(', ')}${Object.keys(r.returned_to || {}).length ? ' — ' + Object.entries(r.returned_to).map(([k, v]) => `${DISPO_LABELS[k] || k}: ${v}`).join(', ') : ''}`}
                                data-testid={`recall-returned-${idx}`}
                              >
                                {r.quantity_returned}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="p-2 text-right font-mono font-semibold">{r.net_quantity}</td>
                          <td className="p-2">{r.use_by_date}</td>
                          <td className="p-2 font-mono text-xs">{r.customer_product_code}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Returned stock — batch-specific paper trail (RTN records) */}
                {recallResults.returned_stock?.length > 0 && (
                  <div className="mt-4 border border-emerald-200 rounded-lg overflow-hidden" data-testid="recall-returned-stock-section">
                    <div className="bg-emerald-50 px-3 py-2 border-b border-emerald-200">
                      <p className="text-sm font-semibold text-emerald-900">
                        Returned Stock — Back on Premises ({recallResults.returned_stock.length} return line(s))
                      </p>
                      <p className="text-xs text-emerald-700">
                        Already deducted from the Net figures above. Recall this stock from the locations below — each RTN has a printable Return Note.
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-emerald-100/50 border-b border-emerald-200">
                            <th className="text-left p-2 font-medium text-emerald-800">Return #</th>
                            <th className="text-left p-2 font-medium text-emerald-800">Date</th>
                            <th className="text-left p-2 font-medium text-emerald-800">Returned From</th>
                            <th className="text-left p-2 font-medium text-emerald-800">Product</th>
                            <th className="text-left p-2 font-medium text-emerald-800">Batch</th>
                            <th className="text-right p-2 font-medium text-emerald-800">Qty</th>
                            <th className="text-left p-2 font-medium text-emerald-800">Now At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recallResults.returned_stock.map((f, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-emerald-50/30'} data-testid={`returned-stock-row-${idx}`}>
                              <td className="p-2 font-mono text-xs font-medium">{f.return_number}</td>
                              <td className="p-2">{f.return_date}</td>
                              <td className="p-2">{f.source_name} <span className="font-mono text-xs text-muted-foreground">({f.dispatch_number})</span></td>
                              <td className="p-2">{f.product_name}</td>
                              <td className="p-2 font-mono text-xs">{f.batch_code || '—'}</td>
                              <td className="p-2 text-right font-mono">{f.quantity_returned}</td>
                              <td className="p-2 font-medium">{f.disposition_label}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                </>
              ) : recallResults ? (
                <p className="text-center text-muted-foreground py-8">No records found for the specified criteria</p>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Select search type and enter criteria, then click &ldquo;Search&rdquo; to generate recall trace report
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recall Email Dialog */}
          <Dialog open={recallEmailDialogOpen} onOpenChange={setRecallEmailDialogOpen}>
            <DialogContent className="max-w-lg" data-testid="recall-email-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-700">
                  <Mail className="w-5 h-5" />
                  Send Recall Notification
                </DialogTitle>
                <DialogDescription>
                  Send recall notification emails to affected customers. Emails are preloaded from search results but can be edited.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="recall-recipients">
                    Recipients <span className="text-xs text-muted-foreground">(comma or newline separated)</span>
                  </Label>
                  <Textarea
                    id="recall-recipients"
                    value={recallEmailRecipients}
                    onChange={(e) => setRecallEmailRecipients(e.target.value)}
                    placeholder="customer1@example.com, customer2@example.com"
                    className="min-h-[100px] font-mono text-sm"
                    data-testid="input-recall-recipients"
                  />
                  <p className="text-xs text-muted-foreground">
                    {recallEmailRecipients.split(/[,;\n]+/).filter(e => e.trim() && e.includes('@')).length} valid email(s) detected
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="recall-reason">
                    Reason for Recall <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="recall-reason"
                    value={recallEmailReason}
                    onChange={(e) => setRecallEmailReason(e.target.value)}
                    placeholder="Please describe the reason for this recall (e.g., potential contamination, mislabeling, quality concern)..."
                    className="min-h-[100px]"
                    data-testid="input-recall-reason"
                  />
                </div>

                <div className="flex items-start gap-2 p-3 bg-zinc-50 border rounded-lg text-sm">
                  <input
                    type="checkbox"
                    id="cb-per-customer-pdf-reports"
                    checked={recallPerCustomerPdf}
                    onChange={(e) => setRecallPerCustomerPdf(e.target.checked)}
                    className="mt-1"
                    data-testid="cb-per-customer-pdf-reports"
                  />
                  <label htmlFor="cb-per-customer-pdf-reports" className="cursor-pointer flex-1">
                    <span className="font-medium">Attach a per-customer PDF to each email</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      Each recipient receives a PDF containing only the records dispatched to their company (with subtotals + grand total) — no other customers' data. Recommended for live recalls.
                    </span>
                  </label>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="font-medium text-amber-800 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Important
                  </p>
                  <p className="text-amber-700 mt-1">
                    This will send {recallEmailRecipients.split(/[,;\n]+/).filter(e => e.trim() && e.includes('@')).length} email(s) using the recall email template configured in Settings. 
                    The email will include details of affected products from the search results.
                  </p>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setRecallEmailDialogOpen(false)} disabled={recallEmailSending}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendRecallEmail} 
                  disabled={recallEmailSending || !recallEmailReason.trim()}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="btn-send-recall-email"
                >
                  {recallEmailSending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Recall Emails
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* OOS Orders Report — every dispatch with at least one Seasonally Out Of Stock line */}
        <TabsContent value="oos" className="mt-6 space-y-4" data-testid="tab-content-oos">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-700" />
                Out-of-Stock Orders
              </CardTitle>
              <CardDescription>
                Every dispatch that contains at least one Seasonally Out Of Stock line. Click a row to jump to that date in the Order Pipeline.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <Label className="text-xs">From Date</Label>
                  <DatePicker
                    value={oosDateFrom}
                    onChange={setOosDateFrom}
                    placeholder="From date"
                    className="w-40"
                  />
                </div>
                <div>
                  <Label className="text-xs">To Date</Label>
                  <DatePicker
                    value={oosDateTo}
                    onChange={setOosDateTo}
                    placeholder="To date"
                    className="w-40"
                  />
                </div>
                <Button size="sm" onClick={loadOOSOrders} data-testid="btn-load-oos" disabled={oosLoading}>
                  {oosLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                  Load
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setOosDateFrom(''); setOosDateTo(''); setOosSearch(''); loadOOSOrders(); }}>
                  Reset
                </Button>
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs">Search (dispatch #, PO, customer, product)</Label>
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={oosSearch}
                      onChange={(e) => setOosSearch(e.target.value)}
                      placeholder="Filter results..."
                      className="pl-7 h-9"
                      data-testid="input-oos-search"
                    />
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportOOSCSV}
                  disabled={!oosOrders?.orders?.length}
                  data-testid="btn-oos-export-csv"
                >
                  <Download className="w-4 h-4 mr-1" />
                  CSV
                </Button>
              </div>

              {oosLoading ? (
                <div className="py-12 text-center text-muted-foreground">
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                  Loading out-of-stock orders…
                </div>
              ) : !oosOrders ? (
                <div className="py-12 text-center text-muted-foreground text-sm">
                  Click <strong>Load</strong> to run the report.
                </div>
              ) : (() => {
                const q = oosSearch.trim().toLowerCase();
                const filtered = q
                  ? (oosOrders.orders || []).filter(o =>
                      (o.dispatch_number || '').toLowerCase().includes(q) ||
                      (o.po_number || '').toLowerCase().includes(q) ||
                      (o.customer_name || '').toLowerCase().includes(q) ||
                      (o.oos_products || []).some(p => p.toLowerCase().includes(q))
                    )
                  : (oosOrders.orders || []);

                if (filtered.length === 0) {
                  return (
                    <div className="py-12 text-center text-muted-foreground" data-testid="oos-empty">
                      <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">
                        {oosOrders.count === 0 ? 'No OOS orders found for the selected range.' : 'No results match your search.'}
                      </p>
                    </div>
                  );
                }

                return (
                  <>
                    <div className="text-xs text-muted-foreground">
                      Showing <strong>{filtered.length}</strong> of <strong>{oosOrders.count}</strong> out-of-stock order{oosOrders.count === 1 ? '' : 's'}.
                    </div>
                    <div className="border rounded-md overflow-hidden">
                      <table className="w-full text-sm" data-testid="oos-orders-table">
                        <thead>
                          <tr className="border-b bg-amber-50 text-amber-900">
                            <th className="px-3 py-2 text-left font-semibold">Dispatch #</th>
                            <th className="px-3 py-2 text-left font-semibold">SO / PO #</th>
                            <th className="px-3 py-2 text-left font-semibold">Customer</th>
                            <th className="px-3 py-2 text-left font-semibold">Dispatch Date</th>
                            <th className="px-3 py-2 text-left font-semibold">Status</th>
                            <th className="px-3 py-2 text-center font-semibold">OOS Lines</th>
                            <th className="px-3 py-2 text-left font-semibold">OOS Products</th>
                            <th className="px-3 py-2 text-right font-semibold">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((o) => (
                            <tr
                              key={o.dispatch_id}
                              className="border-b hover:bg-amber-50/40 cursor-pointer transition-colors"
                              onClick={() => goToOOSOrderInPlanner(o)}
                              data-testid={`oos-row-${o.dispatch_number}`}
                            >
                              <td className="px-3 py-2 font-mono font-semibold">{o.dispatch_number}</td>
                              <td className="px-3 py-2 text-muted-foreground">{o.po_number || '—'}</td>
                              <td className="px-3 py-2">{o.customer_name || '—'}</td>
                              <td className="px-3 py-2">{o.dispatch_date || 'Unscheduled'}</td>
                              <td className="px-3 py-2">
                                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 capitalize">
                                  {(o.status || '').replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-2 rounded bg-amber-100 text-amber-800 text-xs font-bold">
                                  {o.oos_count}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[260px]" title={(o.oos_products || []).join(', ')}>
                                {(o.oos_products || []).join(', ') || '—'}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1 text-xs"
                                  onClick={(e) => { e.stopPropagation(); goToOOSOrderInPlanner(o); }}
                                  data-testid={`oos-goto-${o.dispatch_number}`}
                                  disabled={!o.dispatch_date}
                                  title={o.dispatch_date ? 'Open in Order Pipeline' : 'No dispatch date set'}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  Go to Date
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AccountsIQ Sync Errors — 3 sub-tabs */}
        <TabsContent value="aiq-errors" className="mt-6 space-y-4" data-testid="tab-content-aiq-errors">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Plug className="w-5 h-5 text-indigo-700" />
                AccountsIQ Sync Errors
              </CardTitle>
              <CardDescription>
                Failed pushes (invoices), customer-code mismatches, product-code mismatches.
                Retry one-by-one or mark resolved after a manual fix.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Sub-tab navigation: invoice / customer / product */}
              <div className="flex flex-wrap gap-1">
                {[
                  { key: 'invoice', label: 'Invoices', icon: FileText },
                  { key: 'customer', label: 'Customers', icon: ClipboardCheck },
                  { key: 'product', label: 'Products', icon: Package },
                ].map((c) => {
                  const open = aiqErrors.open_counts?.[c.key] || 0;
                  const Icon = c.icon;
                  return (
                    <Button
                      key={c.key}
                      size="sm"
                      variant={aiqErrorsTab === c.key ? 'default' : 'outline'}
                      className={aiqErrorsTab === c.key ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                      onClick={() => setAiqErrorsTab(c.key)}
                      data-testid={`aiq-subtab-${c.key}`}
                    >
                      <Icon className="w-4 h-4 mr-1.5" />
                      {c.label}
                      {open > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                          {open}
                        </span>
                      )}
                    </Button>
                  );
                })}
                <div className="flex-1" />
                <Select value={aiqErrorsStatus} onValueChange={(v) => setAiqErrorsStatus(v)}>
                  <SelectTrigger className="w-36 h-9" data-testid="aiq-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={loadAiqErrors} disabled={aiqErrorsLoading}>
                  {aiqErrorsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                </Button>
              </div>

              {aiqErrorsLoading ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                  Loading sync errors…
                </div>
              ) : !aiqErrors.rows || aiqErrors.rows.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground" data-testid="aiq-empty">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500 opacity-60" />
                  No {aiqErrorsStatus === 'open' ? 'open' : aiqErrorsStatus} {aiqErrorsTab} sync errors. 🎉
                </div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm" data-testid="aiq-errors-table">
                    <thead>
                      <tr className="border-b bg-indigo-50/50 text-indigo-900">
                        <th className="px-3 py-2 text-left font-semibold">When</th>
                        <th className="px-3 py-2 text-left font-semibold">Dispatch #</th>
                        <th className="px-3 py-2 text-left font-semibold">Error</th>
                        <th className="px-3 py-2 text-center font-semibold">Status</th>
                        <th className="px-3 py-2 text-right font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiqErrors.rows.map((r) => (
                        <tr key={r.id} className="border-b hover:bg-indigo-50/30" data-testid={`aiq-error-row-${r.id}`}>
                          <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                            {r.created_at ? new Date(r.created_at).toLocaleString('en-IE') : '—'}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{r.dispatch_number || r.external_ref || '—'}</td>
                          <td className="px-3 py-2 text-xs max-w-md break-words" title={r.message}>{r.message}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded ${
                              r.status === 'resolved' ? 'bg-emerald-100 text-emerald-700'
                              : r.status === 'retrying' ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                            }`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {r.status === 'open' && (
                              <div className="inline-flex gap-1">
                                {aiqErrorsTab === 'invoice' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleAiqRetry(r)}
                                    disabled={aiqRowBusy === r.id}
                                    data-testid={`aiq-retry-${r.id}`}
                                  >
                                    {aiqRowBusy === r.id
                                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      : <><RotateCcw className="w-3.5 h-3.5 mr-1" />Retry</>}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleAiqResolve(r)}
                                  disabled={aiqRowBusy === r.id}
                                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                  data-testid={`aiq-resolve-${r.id}`}
                                >
                                  <CheckCircle className="w-3.5 h-3.5 mr-1" />Resolve
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Traceability Report */}
        <TabsContent value="traceability" className="mt-6">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Link2 className="w-5 h-5" />
                Traceability Search
              </CardTitle>
              <CardDescription>
                Search dispatches by batch number, product name, use by date, or customer name
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={traceSearch}
                    onChange={(e) => setTraceSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleTraceabilitySearch()}
                    placeholder="Enter batch code, product name, use by date, or customer..."
                    className="pl-10"
                    data-testid="input-traceability-search"
                  />
                </div>
                <Button onClick={handleTraceabilitySearch} disabled={traceLoading} data-testid="btn-trace-search">
                  {traceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="ml-2 hidden sm:inline">Search</span>
                </Button>
                {traceResults.length > 0 && (
                  <Button variant="outline" onClick={handleExportTraceCSV} data-testid="btn-trace-export-csv">
                    <Download className="w-4 h-4 mr-1" /> CSV
                  </Button>
                )}
                {traceResults.length > 0 && (
                  <Button variant="outline" onClick={handleExportTracePDF} data-testid="btn-trace-export-pdf">
                    <FileText className="w-4 h-4 mr-1" /> PDF
                  </Button>
                )}
              </div>

              {traceResults.length > 0 && (
                <div className="space-y-4">
                  {/* Batch-specific totals across every matching order */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trace-summary">
                    <div className="rounded-lg border bg-muted/40 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Batch cases</p>
                      <p className="text-xl font-bold font-mono" data-testid="trace-total-cases">{traceTotals.cases.toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-800">Batch weight (entered)</p>
                      <p className="text-xl font-bold font-mono text-emerald-800" data-testid="trace-total-weight">{traceTotals.kg.toFixed(2)} kg</p>
                    </div>
                    <div className="rounded-lg border bg-muted/40 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Orders</p>
                      <p className="text-xl font-bold font-mono">{traceResults.length}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/40 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Batch codes</p>
                      <p className="text-xl font-bold font-mono">{traceTotals.batchCodes}</p>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Found {traceResults.length} dispatch(es) matching "{traceSearch}" • Figures below are per batch code, not order totals • Click a header to view the full order
                  </div>
                  
                  {traceResults.map((dispatch) => (
                    <Card 
                      key={dispatch.dispatch_id} 
                      className="border-l-4 border-l-primary"
                      data-testid={`dispatch-card-${dispatch.dispatch_id}`}
                    >
                      <CardHeader
                        className="py-3 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => setSelectedDispatch(dispatch)}
                        data-testid={`dispatch-card-header-${dispatch.dispatch_id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex items-center gap-2">
                            <Eye className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <CardTitle className="text-base font-mono">
                                {dispatch.dispatch_number}
                              </CardTitle>
                              <p className="text-sm text-muted-foreground">
                                {dispatch.customer_name} • {dispatch.dispatch_date_display || dispatch.dispatch_date}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs px-2 py-1 rounded ${
                              dispatch.status === 'dispatched' ? 'bg-emerald-100 text-emerald-700' :
                              dispatch.status === 'locked' ? 'bg-amber-100 text-amber-700' :
                              'bg-zinc-100 text-zinc-700'
                            }`}>
                              {dispatch.status?.toUpperCase()}
                            </span>
                            {dispatch.cobranding_name && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Co-brand: {dispatch.cobranding_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="py-0 pb-3 space-y-3">
                        {/* Batch-specific headline for this order */}
                        <div className="flex flex-wrap items-end gap-6">
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Cases (this batch)</p>
                            <p className="text-lg font-bold font-mono" data-testid={`trace-cases-${dispatch.dispatch_id}`}>
                              {num(dispatch.matched_cases).toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-emerald-800">Batch weight entered</p>
                            <p className="text-lg font-bold font-mono text-emerald-800" data-testid={`trace-weight-${dispatch.dispatch_id}`}>
                              {num(dispatch.matched_weight_kg).toFixed(2)} kg
                            </p>
                          </div>
                          <p className="text-xs text-muted-foreground pb-1">
                            PO: {dispatch.po_number || 'N/A'} • {(dispatch.batch_groups?.length || 0)} batch code(s) on this order
                          </p>
                        </div>

                        {/* Sub-section: batch weight actually entered per batch code */}
                        {dispatch.batch_groups?.length > 0 && (
                          <div className="border rounded-lg overflow-x-auto" data-testid={`trace-batch-table-${dispatch.dispatch_id}`}>
                            <table className="w-full text-xs">
                              <thead className="bg-muted/50">
                                <tr>
                                  <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">Batch Code</th>
                                  <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">Product</th>
                                  <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">Format / Pack</th>
                                  <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">Brand</th>
                                  <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">Use By</th>
                                  <th className="text-right px-2 py-1.5 font-medium text-muted-foreground">Cases</th>
                                  <th className="text-right px-2 py-1.5 font-medium text-emerald-800">Batch kg entered</th>
                                  <th className="text-right px-2 py-1.5 font-medium text-muted-foreground">Ordered kg</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {dispatch.batch_groups.map((g, i) => (
                                  <tr key={`${g.batch_code}-${i}`} className="hover:bg-muted/20">
                                    <td className="px-2 py-1.5 font-mono font-medium">{g.batch_code}</td>
                                    <td className="px-2 py-1.5">{g.product_name || '-'}</td>
                                    <td className="px-2 py-1.5 text-muted-foreground">
                                      {[g.product_format, g.pack_size].filter(Boolean).join(' / ') || '-'}
                                    </td>
                                    <td className="px-2 py-1.5">{g.brand || '-'}</td>
                                    <td className="px-2 py-1.5 font-mono">{g.use_by_date || '-'}</td>
                                    <td className="px-2 py-1.5 text-right font-mono">{num(g.cases).toFixed(2)}</td>
                                    <td className="px-2 py-1.5 text-right font-mono font-semibold text-emerald-800">
                                      {num(g.report_weight_kg ?? g.weight_kg).toFixed(2)}
                                    </td>
                                    <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">
                                      {kgOrDash(g.ordered_weight_kg)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Dispatch Detail Modal */}
              <Dialog open={!!selectedDispatch} onOpenChange={(open) => !open && setSelectedDispatch(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dispatch-detail-modal">
                  <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                      <span className="font-mono text-lg">{selectedDispatch?.dispatch_number}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        selectedDispatch?.status === 'dispatched' ? 'bg-emerald-100 text-emerald-700' :
                        selectedDispatch?.status === 'locked' ? 'bg-amber-100 text-amber-700' :
                        'bg-zinc-100 text-zinc-700'
                      }`}>
                        {selectedDispatch?.status?.toUpperCase()}
                      </span>
                    </DialogTitle>
                  </DialogHeader>
                  
                  {selectedDispatch && (
                    <div className="space-y-4">
                      {/* Dispatch Summary */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <Label className="text-xs text-muted-foreground">Customer</Label>
                          <p className="font-medium">{selectedDispatch.customer_name}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Dispatch Date</Label>
                          <p className="font-mono">{selectedDispatch.dispatch_date_display || fmtDate(selectedDispatch.dispatch_date)}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">PO Number</Label>
                          <p className="font-mono">{selectedDispatch.po_number || 'N/A'}</p>
                        </div>
                        {selectedDispatch.cobranding_name && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Co-Branding</Label>
                            <p>{selectedDispatch.cobranding_name}</p>
                          </div>
                        )}
                      </div>

                      {/* Totals — batch match first, whole-order context second */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="text-center rounded-lg border p-2">
                          <p className="text-xs text-muted-foreground">Cases (matched batch)</p>
                          <p className="text-xl font-bold font-mono">{num(selectedDispatch.matched_cases).toFixed(2)}</p>
                        </div>
                        <div className="text-center rounded-lg border border-emerald-200 bg-emerald-50 p-2">
                          <p className="text-xs text-emerald-800">Batch weight entered</p>
                          <p className="text-xl font-bold font-mono text-emerald-800">{num(selectedDispatch.matched_weight_kg).toFixed(2)} kg</p>
                        </div>
                        <div className="text-center rounded-lg border bg-muted/40 p-2">
                          <p className="text-xs text-muted-foreground">Order total (all batches)</p>
                          <p className="text-base font-bold font-mono">{num(selectedDispatch.total_cases).toFixed(2)} cs / {num(selectedDispatch.total_weight).toFixed(2)} kg</p>
                        </div>
                        <div className="text-center rounded-lg border bg-muted/40 p-2">
                          <p className="text-xs text-muted-foreground">Line Items</p>
                          <p className="text-xl font-bold font-mono">{selectedDispatch.all_lines?.length || 0}</p>
                        </div>
                      </div>

                      {/* Line Items */}
                      <div>
                        <Label className="text-xs text-muted-foreground mb-2 block">Line Items</Label>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Product</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Format / Pack</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Batch Code</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Use By</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Qty</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Cases</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Weight</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {selectedDispatch.all_lines?.map((line, idx) => {
                                const isMatch = 
                                  line.batch_code?.toLowerCase().includes(traceSearch.toLowerCase()) ||
                                  line.product_name?.toLowerCase().includes(traceSearch.toLowerCase()) ||
                                  line.use_by_date?.includes(traceSearch);
                                return (
                                  <tr key={idx} className={isMatch ? 'bg-yellow-50' : ''}>
                                    <td className="px-3 py-2">{line.product_name}</td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">
                                      {[line.product_format, line.pack_size].filter(Boolean).join(' / ') || '-'}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">{line.batch_code || '-'}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{fmtDate(line.use_by_date) || '-'}</td>
                                    <td className="px-3 py-2 text-right font-mono">{line.quantity}</td>
                                    <td className="px-3 py-2 text-right font-mono">{Number(line.cases_override ?? line.calculated_cases ?? line.cases ?? 0).toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{Number(line.weight_override ?? line.total_weight ?? line.estimated_weight ?? 0).toFixed(2)} kg</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {traceSearch && (
                          <p className="text-xs text-muted-foreground mt-2">
                            * Yellow highlighted rows match your search term "{traceSearch}"
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <DialogFooter className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setSelectedDispatch(null)}
                      data-testid="btn-close-modal"
                    >
                      Close
                    </Button>
                    <Button 
                      onClick={() => {
                        setSelectedDispatch(null);
                        window.location.href = `/dispatch/${selectedDispatch?.dispatch_id}`;
                      }}
                      data-testid="btn-view-full-dispatch"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Full Dispatch
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              {traceResults.length === 0 && !traceLoading && (
                <div className="text-center py-8 text-muted-foreground">
                  <Link2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Enter a batch code, product name, use by date, or customer name to find related dispatches</p>
                  <p className="text-xs mt-2">Examples: "B20260113", "Cashel Blue", "15/04/2026", "Aldi"</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Orders Report */}
        <TabsContent value="weekly" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-4">
              <CardTitle className="text-base">Weekly Orders Report</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => loadReport('weekly')}>
                  Refresh
                </Button>
                {data && Array.isArray(data) && data.length > 0 && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleExportWeeklyCSV}>
                      <Download className="w-4 h-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportWeeklyPDF}>
                      <Printer className="w-4 h-4 mr-1" /> Print/PDF
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : data && Array.isArray(data) ? (
                <div className="space-y-6">
                  {data.map((week) => (
                    <div key={`${week.year}-${week.week}`} className="border rounded-lg">
                      <div className="px-4 py-3 bg-muted/50 border-b flex justify-between items-center">
                        <h4 className="font-medium">Week {week.week}, {week.year}</h4>
                        <div className="text-sm text-muted-foreground">
                          {week.total_dispatches} dispatches • {week.total_cases?.toFixed(0)} cases • {week.total_weight?.toFixed(2)} kg
                        </div>
                      </div>
                      <div className="p-0">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Dispatch #</th>
                              <th className="text-left px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Customer</th>
                              <th className="text-left px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Date</th>
                              <th className="text-right px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Cases</th>
                              <th className="text-right px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Weight</th>
                              <th className="text-left px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {week.dispatches?.map((d) => (
                              <tr key={d.dispatch_number}>
                                <td className="px-4 py-2 font-mono text-sm">{d.dispatch_number}</td>
                                <td className="px-4 py-2 text-sm">{d.customer_name}</td>
                                <td className="px-4 py-2 font-mono text-sm">{d.dispatch_date}</td>
                                <td className="px-4 py-2 font-mono text-sm text-right">{d.total_cases?.toFixed(2)}</td>
                                <td className="px-4 py-2 font-mono text-sm text-right">{d.total_weight?.toFixed(2)} kg</td>
                                <td className="px-4 py-2 text-sm">{d.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                  {data.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No data for selected period</p>
                  )}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dispatch Summary Report */}
        <TabsContent value="summary" className="mt-6">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">Dispatch Summary Report</CardTitle>
              <div className="flex flex-wrap gap-4 mt-4 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">From</Label>
                  <Input
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className="w-32 font-mono text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To</Label>
                  <Input
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className="w-32 font-mono text-sm"
                  />
                </div>
                <Button size="sm" onClick={() => loadReport('summary')}>Apply</Button>
                {data && data.total_dispatches > 0 && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleExportSummaryCSV}>
                      <Download className="w-4 h-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportSummaryPDF}>
                      <Printer className="w-4 h-4 mr-1" /> Print/PDF
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : data ? (
                <div className="space-y-6">
                  {/* Totals */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">Total Dispatches</p>
                      <p className="text-2xl font-bold font-mono">{data.total_dispatches}</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">Total Cases</p>
                      <p className="text-2xl font-bold font-mono">{data.total_cases?.toFixed(0)}</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">Total Weight</p>
                      <p className="text-2xl font-bold font-mono">{data.total_weight?.toFixed(2)} kg</p>
                    </div>
                  </div>

                  {/* By Customer */}
                  <div>
                    <h4 className="font-medium mb-3">By Customer</h4>
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Customer</th>
                          <th className="text-right px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Dispatches</th>
                          <th className="text-right px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Cases</th>
                          <th className="text-right px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Weight</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {data.by_customer?.map((c) => (
                          <tr key={c.customer}>
                            <td className="px-4 py-2">{c.customer}</td>
                            <td className="px-4 py-2 font-mono text-right">{c.dispatches}</td>
                            <td className="px-4 py-2 font-mono text-right">{c.cases?.toFixed(2)}</td>
                            <td className="px-4 py-2 font-mono text-right">{c.weight?.toFixed(2)} kg</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* By Product */}
                  <div>
                    <h4 className="font-medium mb-3">By Product</h4>
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Product</th>
                          <th className="text-right px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Quantity</th>
                          <th className="text-right px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Cases</th>
                          <th className="text-right px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Weight</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {data.by_product?.map((p) => (
                          <tr key={p.product}>
                            <td className="px-4 py-2">{p.product}</td>
                            <td className="px-4 py-2 font-mono text-right">{p.quantity?.toFixed(2)}</td>
                            <td className="px-4 py-2 font-mono text-right">{p.cases?.toFixed(2)}</td>
                            <td className="px-4 py-2 font-mono text-right">{p.weight?.toFixed(2)} kg</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Batch Register Report */}
        <TabsContent value="batches" className="mt-6">
          <Card>
            <CardHeader className="py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-base">Batch Register</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 sm:flex-initial">
                    <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <Input
                      type="text"
                      placeholder="Search batch / product / date..."
                      value={batchRegSearch}
                      onChange={(e) => { setBatchRegSearch(e.target.value); setBatchRegPage(1); }}
                      className="pl-9 w-full sm:w-72 h-9"
                      data-testid="batch-register-search"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExportCSV}>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (() => {
                if (!data || !Array.isArray(data) || data.length === 0) {
                  return <p className="text-center text-muted-foreground py-8">No batches found</p>;
                }

                // Client-side filter — case-insensitive substring across the visible columns
                const q = (batchRegSearch || '').trim().toLowerCase();
                const filtered = q
                  ? data.filter(b => {
                      const haystack = [
                        b.batch_code, b.product_name,
                        b.production_date, b.use_by_date
                      ].filter(Boolean).join(' ').toLowerCase();
                      return haystack.includes(q);
                    })
                  : data;

                const totalPages = Math.max(1, Math.ceil(filtered.length / BATCH_REG_PAGE_SIZE));
                const safePage = Math.min(batchRegPage, totalPages);
                const startIdx = (safePage - 1) * BATCH_REG_PAGE_SIZE;
                const pageRows = filtered.slice(startIdx, startIdx + BATCH_REG_PAGE_SIZE);

                if (filtered.length === 0) {
                  return (
                    <p className="text-center text-muted-foreground py-8" data-testid="batch-register-no-match">
                      No batches match "{batchRegSearch}"
                    </p>
                  );
                }

                return (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Batch</th>
                            <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Product</th>
                            <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Production</th>
                            <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Use By</th>
                            <th className="text-right px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Produced</th>
                            <th className="text-right px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Available</th>
                            <th className="text-right px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Allocated</th>
                            <th className="text-right px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Dispatched</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {pageRows.map((batch) => (
                            <tr key={batch.batch_code} className="hover:bg-muted/30" data-testid={`batch-register-row-${batch.batch_code}`}>
                              <td className="px-4 py-2 font-mono font-medium">{batch.batch_code}</td>
                              <td className="px-4 py-2">{batch.product_name}</td>
                              <td className="px-4 py-2 font-mono text-sm">{batch.production_date}</td>
                              <td className="px-4 py-2 font-mono text-sm">{batch.use_by_date}</td>
                              <td className="px-4 py-2 font-mono text-right">{batch.quantity_produced?.toFixed(2)}</td>
                              <td className="px-4 py-2 font-mono text-right">{batch.quantity_available?.toFixed(2)}</td>
                              <td className="px-4 py-2 font-mono text-right">{batch.quantity_allocated?.toFixed(2)}</td>
                              <td className="px-4 py-2 font-mono text-right">{batch.quantity_dispatched?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination footer */}
                    <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
                      <span className="text-muted-foreground" data-testid="batch-register-page-info">
                        Showing <span className="font-medium">{startIdx + 1}</span>–<span className="font-medium">{Math.min(startIdx + BATCH_REG_PAGE_SIZE, filtered.length)}</span> of <span className="font-medium">{filtered.length}</span>
                        {q && <> (filtered from <span className="font-medium">{data.length}</span>)</>}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => setBatchRegPage(p => Math.max(1, p - 1))}
                          disabled={safePage <= 1}
                          data-testid="batch-register-prev"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Prev
                        </Button>
                        <span className="text-muted-foreground">Page {safePage} / {totalPages}</span>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => setBatchRegPage(p => Math.min(totalPages, p + 1))}
                          disabled={safePage >= totalPages}
                          data-testid="batch-register-next"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Label Prints Report */}
        <TabsContent value="labels" className="mt-6">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">Label Print History</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : data && Array.isArray(data) && data.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Dispatch</th>
                      <th className="text-left px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Product</th>
                      <th className="text-left px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Batch</th>
                      <th className="text-right px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Print Count</th>
                      <th className="text-left px-4 py-2 text-xs font-medium uppercase text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.map((label, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 font-mono">{label.dispatch_number}</td>
                        <td className="px-4 py-2">{label.product_name}</td>
                        <td className="px-4 py-2 font-mono">{label.batch_code}</td>
                        <td className="px-4 py-2 font-mono text-right">{label.print_count}</td>
                        <td className="px-4 py-2">{label.print_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center text-muted-foreground py-8">No label prints recorded yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales by Category Report */}
        <TabsContent value="sales-category" className="mt-6 space-y-4">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Tag className="w-5 h-5" />
                Sales by Category Report
              </CardTitle>
              <CardDescription>
                View sales aggregated by product category with dispatch details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">Period</Label>
                  <Select value={salesByCatPeriod} onValueChange={setSalesByCatPeriod}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Today</SelectItem>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {salesByCatPeriod === 'custom' && (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs">Start Date</Label>
                      <Input 
                        type="date" 
                        value={salesByCatDateFrom} 
                        onChange={(e) => setSalesByCatDateFrom(e.target.value)}
                        className="w-36"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">End Date</Label>
                      <Input 
                        type="date" 
                        value={salesByCatDateTo} 
                        onChange={(e) => setSalesByCatDateTo(e.target.value)}
                        className="w-36"
                      />
                    </div>
                  </>
                )}
                <Button onClick={loadSalesByCategoryReport} disabled={salesByCatLoading}>
                  {salesByCatLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Generate Report
                </Button>
                {salesByCatReport && (
                  <Button variant="outline" onClick={downloadSalesByCategoryPdf}>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                )}
              </div>

              {/* Report Period Info */}
              {salesByCatReport && (
                <div className="text-sm text-muted-foreground">
                  Report period: {salesByCatReport.start_date} to {salesByCatReport.end_date}
                </div>
              )}

              {/* Report Data */}
              {salesByCatLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : salesByCatReport?.categories?.length > 0 ? (
                <div className="space-y-4">
                  {salesByCatReport.categories.map(category => (
                    <Card key={category.category_name} className="overflow-hidden">
                      <div 
                        className="flex items-center justify-between p-4 bg-muted/30 cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleCategoryExpand(category.category_name)}
                      >
                        <div className="flex items-center gap-3">
                          {expandedCategories.has(category.category_name) ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                          <div>
                            <h3 className="font-semibold text-lg">{category.category_name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {category.products.length} products • {category.total_orders} orders
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold font-mono">{category.total_qty.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Total Units</p>
                        </div>
                      </div>
                      
                      {expandedCategories.has(category.category_name) && (
                        <div className="p-4 space-y-3">
                          {category.products.map(product => (
                            <div key={product.product_id} className="border rounded-lg overflow-hidden">
                              <div 
                                className="flex items-center justify-between p-3 bg-white cursor-pointer hover:bg-gray-50"
                                onClick={() => toggleProductExpand(product.product_id)}
                              >
                                <div className="flex items-center gap-2">
                                  {expandedProducts.has(product.product_id) ? (
                                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                  )}
                                  <span className="font-medium">{product.product_name}</span>
                                  {product.product_sku && (
                                    <span className="text-xs text-muted-foreground font-mono">({product.product_sku})</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-6 text-sm">
                                  <div className="text-right">
                                    <span className="font-mono font-semibold">{product.total_qty.toLocaleString()}</span>
                                    <span className="text-muted-foreground ml-1">units</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="font-mono">{product.order_count}</span>
                                    <span className="text-muted-foreground ml-1">orders</span>
                                  </div>
                                </div>
                              </div>
                              
                              {expandedProducts.has(product.product_id) && product.details?.length > 0 && (
                                <div className="border-t bg-gray-50/50">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b bg-gray-100/50">
                                        <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Dispatch #</th>
                                        <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Customer</th>
                                        <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Qty</th>
                                        <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Date</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                      {product.details.slice(0, 20).map((detail, idx) => (
                                        <tr key={idx} className="hover:bg-white">
                                          <td className="px-3 py-2 font-mono text-blue-600">{detail.dispatch_number}</td>
                                          <td className="px-3 py-2">{detail.customer_name}</td>
                                          <td className="px-3 py-2 text-right font-mono">{detail.qty}</td>
                                          <td className="px-3 py-2 text-muted-foreground">{detail.date}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {product.details.length > 20 && (
                                    <div className="text-center py-2 text-xs text-muted-foreground">
                                      Showing 20 of {product.details.length} entries
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              ) : salesByCatReport ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Tag className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No sales data found for the selected period</p>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Tag className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Select a period and click "Generate Report" to view sales by category</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Report */}

        {/* Transport Report */}
        <TabsContent value="transport" className="mt-6 space-y-4">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Transport Agent Weekly Report
              </CardTitle>
              <CardDescription>
                Generate transport reports by date range, sorted by transport agent. Shows customer, delivery address, PO, case count, and weights. Download as Excel or CSV to attach to emails.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Start Date</Label>
                  <Input
                    type="date"
                    value={transportDateFrom}
                    onChange={(e) => setTransportDateFrom(e.target.value)}
                    data-testid="transport-date-from"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">End Date</Label>
                  <Input
                    type="date"
                    value={transportDateTo}
                    onChange={(e) => setTransportDateTo(e.target.value)}
                    data-testid="transport-date-to"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Transport Agent</Label>
                  <Select value={transportAgent} onValueChange={setTransportAgent}>
                    <SelectTrigger data-testid="transport-agent-filter">
                      <SelectValue placeholder="All agents" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agents</SelectItem>
                      {transportOptions.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => downloadTransportReport('excel')}
                    disabled={transportLoading || !transportDateFrom || !transportDateTo}
                    className="flex-1"
                    data-testid="transport-download-excel"
                  >
                    {transportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Excel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => downloadTransportReport('csv')}
                    disabled={transportLoading || !transportDateFrom || !transportDateTo}
                    data-testid="transport-download-csv"
                  >
                    CSV
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Report includes: Dispatch Date, Dispatch #, Customer, Delivery Address, PO Number, Total Cases, Net Weight, Gross Weight — grouped and subtotalled by Transport Agent.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Report */}
        {canManageUsers(user?.role) && (
          <TabsContent value="audit" className="mt-6 space-y-4">
            {/* Stats Card */}
            {auditStats && (
              <Card>
                <CardContent className="py-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Logs</p>
                      <p className="text-2xl font-bold font-mono">{auditStats.total_logs?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Oldest Log</p>
                      <p className="text-sm font-mono">{auditStats.oldest_log ? formatDateTime(auditStats.oldest_log) : '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Newest Log</p>
                      <p className="text-sm font-mono">{auditStats.newest_log ? formatDateTime(auditStats.newest_log) : '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Retention</p>
                      <p className="text-lg font-bold">{auditStats.retention_days} days</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Eligible for Cleanup</p>
                      <p className="text-lg font-bold text-amber-600">{auditStats.logs_eligible_for_cleanup?.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Filters */}
            <Card>
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Filter className="w-4 h-4" /> Filters
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setAuditFilters({ action: '', entity_type: '', user_email: '', date_from: '', date_to: '' });
                        setAuditPage(0);
                        loadReport('audit');
                      }}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Reset
                    </Button>
                    {user?.role === 'super_admin' && auditStats?.logs_eligible_for_cleanup > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                          if (window.confirm(`Delete ${auditStats.logs_eligible_for_cleanup} logs older than ${auditStats.retention_days} days?`)) {
                            try {
                              const res = await cleanupAuditLogs();
                              toast.success(res.data.message);
                              loadReport('audit');
                            } catch (err) {
                              toast.error('Failed to cleanup logs');
                            }
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Cleanup Old Logs
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">Action</Label>
                    <Select
                      value={auditFilters.action}
                      onValueChange={(val) => setAuditFilters({ ...auditFilters, action: val === 'all' ? '' : val })}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All Actions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Actions</SelectItem>
                        {auditFilterOptions.actions.map(a => (
                          <SelectItem key={a} value={a}>{a}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Entity Type</Label>
                    <Select
                      value={auditFilters.entity_type}
                      onValueChange={(val) => setAuditFilters({ ...auditFilters, entity_type: val === 'all' ? '' : val })}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All Entities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Entities</SelectItem>
                        {auditFilterOptions.entity_types.map(e => (
                          <SelectItem key={e} value={e}>{e}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">User</Label>
                    <Select
                      value={auditFilters.user_email}
                      onValueChange={(val) => setAuditFilters({ ...auditFilters, user_email: val === 'all' ? '' : val })}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All Users" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        {auditFilterOptions.user_emails.map(u => (
                          <SelectItem key={u} value={u}>{u}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">From Date</Label>
                    <DatePicker
                      value={auditFilters.date_from}
                      onChange={(val) => setAuditFilters({ ...auditFilters, date_from: val })}
                      placeholder="From date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">To Date</Label>
                    <DatePicker
                      value={auditFilters.date_to}
                      onChange={(val) => setAuditFilters({ ...auditFilters, date_to: val })}
                      placeholder="To date"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Button
                    size="sm"
                    onClick={() => { setAuditPage(0); loadReport('audit'); }}
                  >
                    <Search className="w-4 h-4 mr-1" /> Apply Filters
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Audit Log Table */}
            <Card>
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Audit Log</CardTitle>
                  <div className="text-sm text-muted-foreground">
                    Showing {data?.length || 0} of {auditTotal.toLocaleString()} logs
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : data && Array.isArray(data) && data.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Timestamp</th>
                            <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">User</th>
                            <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Action</th>
                            <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Entity</th>
                            <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Entity ID</th>
                            <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {data.map((log) => (
                            <tr key={log.id} className="hover:bg-muted/30">
                              <td className="px-4 py-2 font-mono text-xs">{formatDateTime(log.timestamp)}</td>
                              <td className="px-4 py-2 text-sm">{log.user_email}</td>
                              <td className="px-4 py-2">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  log.action === 'create' ? 'bg-emerald-100 text-emerald-700' :
                                  log.action === 'update' ? 'bg-blue-100 text-blue-700' :
                                  log.action === 'delete' ? 'bg-red-100 text-red-700' :
                                  log.action === 'login' ? 'bg-purple-100 text-purple-700' :
                                  'bg-zinc-100 text-zinc-700'
                                }`}>
                                  {log.action}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm">{log.entity_type}</td>
                              <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{log.entity_id?.slice(0, 8)}...</td>
                              <td className="px-4 py-2 text-xs text-muted-foreground max-w-xs truncate">
                                {log.after_value ? JSON.stringify(log.after_value).slice(0, 80) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    <div className="flex items-center justify-between p-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        Page {auditPage + 1} of {Math.ceil(auditTotal / AUDIT_PAGE_SIZE) || 1}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={auditPage === 0}
                          onClick={() => { setAuditPage(p => p - 1); loadReport('audit'); }}
                        >
                          <ChevronLeft className="w-4 h-4" /> Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={(auditPage + 1) * AUDIT_PAGE_SIZE >= auditTotal}
                          onClick={() => { setAuditPage(p => p + 1); loadReport('audit'); }}
                        >
                          Next <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No audit logs found matching filters</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
